import { LIVE_RELEASE_ENV } from '../components/create-run/constants';
import type { CalibrationRun } from './types';

export const CANCELED_TOOLTIP = 'This run was manually canceled. No results generated.';
export const GENERIC_FAILURE_TOOLTIP = 'Run failed due to an unexpected error. Contact support.';

/**
 * A run needs a minimum number of usable conversations before analysis can
 * start; below that the backend fails it outright. The wording differs by how
 * the conversations were gathered — a live run *listens* as they happen, a
 * historical run *replays* ones already recorded.
 */
function insufficientConversationsTooltip(run: CalibrationRun): string | null {
  const processed = Number(run.conversationCount);
  const required = Number(run.conversationCountThreshold);
  // Both numbers have to be real for the sentence to say anything useful —
  // "Not enough conversations — NaN listened to" is worse than the generic copy.
  if (!Number.isFinite(processed) || !Number.isFinite(required) || required <= 0) return null;
  if (processed >= required) return null;

  const gathered =
    run.evaluationEnvironment === LIVE_RELEASE_ENV
      ? `${processed} listened to`
      : `${processed} replayed successfully`;
  return `Not enough conversations — ${gathered}, ${required} needed to process a run.`;
}

/**
 * Hover copy for the info icon beside a run's status badge, or null when that
 * status needs no explanation (the badge alone says it).
 *
 * FAILED covers two very different situations the badge can't distinguish:
 * too few conversations to analyse — which is actionable, so it names the
 * shortfall — versus anything else, which isn't.
 */
export function getRunStatusTooltip(run: CalibrationRun): string | null {
  if (run.status === 'CANCELLED') return CANCELED_TOOLTIP;
  if (run.status === 'FAILED') {
    return insufficientConversationsTooltip(run) ?? GENERIC_FAILURE_TOOLTIP;
  }
  return null;
}
